// [AI_START::module id="getMediaSource_module" purpose="Build authenticated media-dispatcher URLs without requesting a viewport upscale" links=["getChatImageSource", "findLightboxPhotoIndex"]]
import { MediaParams } from "~@/api";
import calculateMediaSize from "../calculateMeidaSize";

type GetMediaSource = (args: MediaParams) => string;

type GetChatImageSourceArgs = {
  url: string;
  token: string;
  width?: number | null;
  height?: number | null;
  maxWidth: number;
  maxHeight: number;
};

// [AI_START::function id="getMediaSource" purpose="Skip the request until a JWT exists so img src never hits get_file unauthorized" links=["getChatImageSource"]]
const getMediaSource: GetMediaSource = ({
  url,
  width,
  height,
  quality,
  upscale,
  token,
}) => {
  if (!token) {
    return "";
  }

  return `${
    process.env.REACT_APP_LAMBDAS_ENDPOINT
  }/media_dispatcher/get_file?path=${url}&width=${Math.round(
    width
  )}&height=${Math.round(
    height
  )}&upscale=${upscale}&quality=${quality}&authorization=Bearer ${token}`;
};
// [AI_END::getMediaSource]

// [AI_START::function id="getChatImageSource" purpose="Fit chat photos to the feed or lightbox without upscaling small JPEGs" links=["getMediaSource", "calculateMediaSize"]]
export const getChatImageSource = ({
  url,
  token,
  width,
  height,
  maxWidth,
  maxHeight,
}: GetChatImageSourceArgs): string => {
  if (!token || !url) {
    return "";
  }

  const size = calculateMediaSize({
    width: width || maxWidth,
    height: height || maxHeight,
    maxWidth,
    maxHeight,
  });

  return getMediaSource({
    type: "chat_room",
    url,
    width: size.width,
    height: size.height,
    quality: 80,
    upscale: false,
    token,
  });
};
// [AI_END::getChatImageSource]

// [AI_START::function id="getMediaDispatcherPath" purpose="Compare chat preview and lightbox srcs by storage path, ignoring size query params" links=["findLightboxPhotoIndex", "getChatImageSource"]]
export const getMediaDispatcherPath = (src: string): string => {
  if (!src) {
    return "";
  }

  try {
    const parsed = new URL(src, "https://placeholder.local");
    return parsed.searchParams.get("path") || src;
  } catch {
    return src;
  }
};
// [AI_END::getMediaDispatcherPath]

// [AI_START::function id="findLightboxPhotoIndex" purpose="Open lightbox for the clicked preview even when feed and fullscreen request different widths" links=["getMediaDispatcherPath", "MessageGallery"]]
export const findLightboxPhotoIndex = (
  photos: string[],
  previewSrc: string
): number => {
  if (!previewSrc) {
    return -1;
  }

  const exactIndex = photos.indexOf(previewSrc);
  if (exactIndex !== -1) {
    return exactIndex;
  }

  const previewPath = getMediaDispatcherPath(previewSrc);
  return photos.findIndex((photo) => getMediaDispatcherPath(photo) === previewPath);
};
// [AI_END::findLightboxPhotoIndex]

export default getMediaSource;
// [AI_END::getMediaSource_module]
